import { Button } from '@lemon-io/ui-kit';
import { Box, CircularProgress, styled, Typography } from '@mui/material';

export const ParsingStepsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
}));

export const ParsingStepRow = styled(Box)<{
  $status: 'idle' | 'spinning' | 'done';
  $borderless?: boolean;
}>(({ theme, $status, $borderless }) => {
  const idleBorder = `2px solid ${theme.palette.contrast.opacity15}`;
  const transparentBorder = '2px solid transparent';
  const isSpinning = $status === 'spinning';
  const showBorder = !$borderless && (!isSpinning || theme.nonGated);
  const spinningBg = theme.nonGated
    ? theme.palette.common.white
    : theme.palette.secondary.opacity15;

  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(2, 3),
    border: showBorder ? idleBorder : transparentBorder,
    backgroundColor: isSpinning ? spinningBg : theme.palette.common.white,
    outline:
      !$borderless && isSpinning && !theme.nonGated
        ? `3px solid ${theme.palette.secondary.main}`
        : '3px solid transparent',
    outlineOffset: '-3px',
    zIndex: $status === 'spinning' ? 1 : 0,
    transition: 'background-color 0.3s, outline-color 0.3s',

    '&:first-of-type': {
      borderTopLeftRadius: theme.spacing(1),
      borderTopRightRadius: theme.spacing(1),
    },

    '&:last-of-type': {
      borderBottomLeftRadius: theme.spacing(1),
      borderBottomRightRadius: theme.spacing(1),
    },

    '&:not(:first-of-type)': {
      marginTop: '-2px',
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
    },
  };
});

export const ParsingStepText = styled(Typography)<{
  $status: 'idle' | 'spinning' | 'done';
}>(({ theme, $status }) => ({
  color:
    $status === 'done'
      ? theme.palette.success.main
      : theme.palette.text.primary,
  fontWeight: 600,
  transition: 'color 0.3s',
  flex: 1,
  fontSize: 22,
  lineHeight: '28px',
  padding: theme.spacing(1, 0),

  [theme.breakpoints.down('sm')]: {
    fontSize: 20,
    lineHeight: '24px',
  },

  ...(theme.nonGated && {
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '24px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 15,
      lineHeight: '22px',
    },
  }),
}));

export const StepIconBox = styled(Box)({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minWidth: 28,
});

export const NumberBadge = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: theme.palette.common.black,
  color: theme.palette.common.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1,
}));

export const StepSpinner = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

export const Wrapper = styled(Box)({
  display: 'flex',
  width: '100%',
});

export const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '75%',
  flexDirection: 'column',

  padding: theme.spacing(11.5, 5.5),

  [theme.breakpoints.up(1920)]: {
    padding: theme.spacing(20, 10),
  },

  [theme.breakpoints.up(2560)]: {
    padding: theme.spacing(30, 15),
  },

  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(6),
    padding: theme.spacing(6, 0, 0, 0),
  },

  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const AdditionalContent = styled(Box)(({ theme }) => ({
  width: '25%',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(0, 3),

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const Root = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: theme.palette.common.white,

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: theme.spacing(0, 2.8125),
    minHeight: 'calc(100% - 76px)',
  },
}));

export const InfoCardCenter = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
}));

export const Fields = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(3),
  marginTop: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    rowGap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
}));

export const ButtonsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    rowGap: theme.spacing(2),
  },
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));
